// Pure read-side helpers over the outbox for the referee score screens
// (native app; the PWA keeps its own copies in apps/referee). No storage
// access here — callers pass the entries they read from their SyncStore.
import { assignmentOf } from './outbox.js';
import type { CachedShot, OutboxEntry } from './store.js';

/**
 * "What · why" for a change the server rejected (PWA `failedReason`).
 * `lastError` is the server's German reason; entries rejected with only a
 * status carry `http_4xx`. `penaltyLabel` names a penalty code (the app
 * passes `penaltyCodeLabel` from @flm9/i18n — not a dependency here).
 */
export function failedReason(
  entry: OutboxEntry,
  shots: readonly CachedShot[],
  penaltyLabel?: (code: string) => string,
): string {
  const payload = (entry.payload ?? {}) as {
    ord?: number;
    clientIdempotencyKey?: string;
    code?: string;
  };
  const ord =
    payload.ord ?? shots.find((s) => s.clientIdempotencyKey === payload.clientIdempotencyKey)?.ord;
  const shot = ord != null ? `Schuss #${ord}` : 'Schuss';
  const labels: Partial<Record<OutboxEntry['op'], string>> = {
    'shot.add': shot,
    'shot.update': `${shot} (Korrektur)`,
    'shot.remove': `${shot} (Löschen)`,
    'round.timing': 'Zeit',
    'round.complete': 'Bestätigung',
    'round.call': 'Aufruf',
    'penalty.add':
      payload.code && penaltyLabel ? `Strafe ${penaltyLabel(payload.code)}` : 'Strafe',
    'penalty.remove': 'Strafe (Löschen)',
    'round.dq': 'PAR-Disqualifikation',
  };
  const what = labels[entry.op] ?? entry.op;
  const why =
    !entry.lastError || /^http_\d+$/.test(entry.lastError)
      ? `vom Server abgelehnt${entry.lastError ? ` (${entry.lastError.replace('http_', 'HTTP ')})` : ''}`
      : entry.lastError;
  return `${what}: ${why}`;
}

/** Round timing as still queued in the outbox (oldest first, later values win). */
export interface QueuedTiming {
  firstShotTimeMs?: number;
  elapsedTimeMs?: number;
}

/** Everything the score screen shows about one shooter's unsent changes. */
export interface AssignmentOutboxView {
  /** Entries still on their way (pending + inflight). */
  pending: number;
  /** Entries the server rejected (failed_terminal). */
  failed: number;
  /** One "what: why" line per rejected entry. */
  failedReasons: string[];
  /** Idempotency keys of shots with a queued change (amber dot per slot, REF-9). */
  unsentKeys: Set<string>;
  /** A confirmation of this round is queued (offline Bestätigen, REF-3). */
  confirmQueued: boolean;
  /** Unsent round timing — wins over the server value after a remount. */
  queuedTiming: QueuedTiming;
  /** Something of this round waits for a new sign-in (REF-10). */
  authPaused: boolean;
  /**
   * A disqualification (PAR auto-DQ, `round.dq`) of this round is queued but
   * not on the server yet (offline / behind unsent changes). The screens
   * treat the round as DQ'd at once — terminal restrictions, 0 points, no
   * HF — and it survives a remount; the newest queued one wins.
   */
  queuedDq: QueuedDq | null;
}

/** A queued `round.dq` (reason + whether it is terminal, hart-stopp). */
export interface QueuedDq {
  reason: string;
  terminal: boolean;
}

/**
 * Build the view for one assignment from all unresolved entries.
 * `open` = pending + inflight, `rejected` = failed_terminal.
 * `authMarker` = the `lastError` a paused-by-expired-login entry carries.
 */
export function assignmentOutboxView(
  assignmentId: string,
  open: readonly OutboxEntry[],
  rejected: readonly OutboxEntry[],
  shots: readonly CachedShot[],
  authMarker: string,
  penaltyLabel?: (code: string) => string,
): AssignmentOutboxView {
  const mineOpen = open.filter((e) => assignmentOf(e) === assignmentId);
  const mineRejected = rejected.filter((e) => assignmentOf(e) === assignmentId);
  const unsentKeys = new Set<string>();
  for (const e of [...mineOpen, ...mineRejected]) {
    if (!e.op.startsWith('shot.')) continue;
    const key = (e.payload as { clientIdempotencyKey?: string } | null)?.clientIdempotencyKey;
    if (key) unsentKeys.add(key);
  }
  const queuedTiming: QueuedTiming = {};
  let queuedDq: QueuedDq | null = null;
  for (const e of [...mineOpen].sort((a, b) => a.createdAt - b.createdAt)) {
    if (e.op === 'round.dq') {
      const p = (e.payload ?? {}) as { reason?: unknown; terminal?: unknown };
      queuedDq = {
        reason: typeof p.reason === 'string' ? p.reason : '',
        terminal: p.terminal === true,
      };
      continue;
    }
    if (e.op !== 'round.timing') continue;
    const p = e.payload as QueuedTiming;
    if (p.firstShotTimeMs !== undefined) queuedTiming.firstShotTimeMs = p.firstShotTimeMs;
    if (p.elapsedTimeMs !== undefined) queuedTiming.elapsedTimeMs = p.elapsedTimeMs;
  }
  return {
    pending: mineOpen.length,
    failed: mineRejected.length,
    failedReasons: mineRejected.map((e) => failedReason(e, shots, penaltyLabel)),
    unsentKeys,
    confirmQueued: mineOpen.some((e) => e.op === 'round.complete'),
    queuedTiming,
    authPaused: mineOpen.some((e) => e.lastError === authMarker),
    queuedDq,
  };
}

/**
 * Shooters whose call ("Aufrufen", `round.call`) is still queued offline →
 * the lane it asked for (`null` = left to the server). The roster shows them
 * as called by this referee until the server confirms it.
 */
export function queuedCallLanes(open: readonly OutboxEntry[]): Map<string, string | null> {
  const calls = new Map<string, string | null>();
  for (const e of open) {
    if (e.op !== 'round.call') continue;
    const id = assignmentOf(e);
    if (!id) continue;
    const laneId = (e.payload as { laneId?: string | null } | null)?.laneId;
    calls.set(id, typeof laneId === 'string' && laneId ? laneId : null);
  }
  return calls;
}

/**
 * Shooters whose confirmation is still queued (PWA `queuedConfirmations`).
 * Until it lands the server (and any roster fetched meanwhile) still shows
 * them as open — "who is next" must not pick them again.
 */
export function queuedConfirmationIds(open: readonly OutboxEntry[]): Set<string> {
  const ids = new Set<string>();
  for (const e of open) {
    if (e.op !== 'round.complete') continue;
    const id = assignmentOf(e);
    if (id) ids.add(id);
  }
  return ids;
}

/**
 * Unsent (`pending` + `inflight`) and rejected (`failed_terminal`) entries of
 * the given shooters — e.g. all rounds of one session before Badeschluss.
 * `ids` null = device-wide. Other statuses (acked) are ignored, so callers
 * may pass one read of every unresolved entry.
 */
export function unsentCountsFor(
  entries: readonly OutboxEntry[],
  ids: ReadonlySet<string> | null,
): { pending: number; failed: number } {
  let pending = 0;
  let failed = 0;
  for (const e of entries) {
    if (ids != null && !ids.has(assignmentOf(e) ?? '')) continue;
    if (e.status === 'pending' || e.status === 'inflight') pending++;
    else if (e.status === 'failed_terminal') failed++;
  }
  return { pending, failed };
}
